import { useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { DataTable } from "@/components/data-table";
import { useGlobalLoader } from "@/components/GlobalLoader";
import { useImpressao } from "@/components/ImpressaoComponent";
import { MODOS_IMPRESSAO } from "@/lib/modosImpressao";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { AdminContext } from "@/contexts/AdminContext";
import { useCategorias } from "@/contexts/CategoriasContext";
import { useContaConfig } from "@/contexts/ContaConfigContext";
import { useEpc } from "@/contexts/EpcContext";
import { useImpressaoConfig } from "@/contexts/ImpressaoConfigContext";
import { useItem } from "@/contexts/ItemContext";
import { useLocalizacoes } from "@/contexts/LocalizacoesContext";
import { useSeriais } from "@/contexts/SeriaisContext";
import { aplicarPreDefinicoesUnicas } from "@/components/InfoComplField";
import type { CategoriaItemResponse } from "@/types/categoria-item-response";
import type { ImpressaoItem } from "@/types/impressao-item";
import type { NewCategoriaItem } from "@/types/new-categoria-item";
import {
  brDateToISO,
  sanitizarCodigoHex,
  verificarEpcsInvalidos,
  verificarVariaveisDeSubstituicaoEmInfosComplementares,
} from "@/utils/UtilFuncoes";
import {
  analisarMacroXml,
  avaliarMacroComFallback,
  formatarResultadoMacro,
  macroUsaInfoCategoria,
  numeroDeTexto,
  type MacroXml,
  type OperandoMacro,
} from "@/utils/xmlMacros";
import type { ColumnDef } from "@tanstack/react-table";
import { alert } from "@/contexts/AlertModalContext";
import { LocalizacaoCombobox } from "@/components/LocalizacaoCombobox";
import {
  InfoIndividualEmMassa,
  aplicarInfoIndividual,
  dataParaInput,
  valoresDaCategoria,
} from "@/components/InfoIndividualEmMassa";
import {
  AlertTriangle,
  Eye,
  FileCode2,
  Layers,
  MapPin,
  Printer,
  RefreshCw,
  Upload,
  X,
} from "lucide-react";

// Unidades de medida pré-definidas (evita digitação livre na importação).
const UNIDADES_MEDIDA = [
  "un",
  "kg",
  "g",
  "mg",
  "t",
  "L",
  "mL",
  "m",
  "cm",
  "mm",
  "m²",
  "m³",
  "cx",
  "pç",
  "par",
  "dz",
] as const;

type XmlItem = {
  codigo: string;
  descricao: string;
  inf_compl_1: string;
  inf_compl_2: string;
  inf_compl_3: string;
  inf_compl_4: string;
  inf_compl_5: string;
  inf_compl_6: string;
  inf_compl_7: string;
  inf_compl_8: string;
  inf_compl_9: string;
  inf_compl_10: string;
  inf_compl_11: string;
  inf_compl_12: string;
  quantidade: string;
  valor: string;
  valor_unidade: string;
  dt_validade: string;
};

// Campos de informação individual do item do XML (validade em AAAA-MM-DD,
// valor em texto), para o passo de informação individual em massa.
const CAMPOS_XML = {
  quantidade: "quantidade",
  valor: "valor",
  validade: "dt_validade",
  formatoData: "iso",
  valorComo: "string",
} as const;

function getElementText(parent: Element, tagName: string): string {
  if (!tagName) return "";
  const el = parent.querySelector(tagName);
  if (el) return el.textContent?.trim() ?? "";
  return parent.getAttribute(tagName) ?? "";
}

/**
 * Igual ao `getElementText`, mas quando a tag não existe dentro do elemento do
 * item sobe pelos ancestrais até achá-la. É o que permite usar campos de
 * cabeçalho da nota (`nNF`, `serie`, `xNome` do emitente...), que ficam fora do
 * `<det>`/`<prod>`. Candidatos que estejam dentro de OUTRO item são ignorados,
 * pra nunca puxar o valor do item vizinho quando a tag falta só neste item.
 */
function getElementTextComEscopo(
  el: Element,
  tagName: string,
  itensXml: Element[],
): string {
  if (!tagName) return "";
  const direto = getElementText(el, tagName);
  if (direto) return direto;

  let ancestral = el.parentElement;
  while (ancestral) {
    const achado = Array.from(ancestral.querySelectorAll(tagName)).find(
      (cand) => !itensXml.some((item) => item !== el && item.contains(cand)),
    );
    if (achado) {
      const texto = achado.textContent?.trim() ?? "";
      if (texto) return texto;
    }
    const attr = ancestral.getAttribute(tagName);
    if (attr) return attr;
    ancestral = ancestral.parentElement;
  }
  return "";
}

// Popover de qtd/valor/validade por item — mesmo padrão do "Info individual"
// da Importar Pedidos. Extraído pra fora do `cell:` por causa dos hooks.
function XmlInfoIndividualCell({
  row,
  setXmlItens,
}: {
  row: { index: number; original: XmlItem };
  setXmlItens: React.Dispatch<React.SetStateAction<XmlItem[]>>;
}) {
  const { quantidade, valor, dt_validade } = row.original;
  const [open, setOpen] = useState(false);
  const [qtd, setQtd] = useState(quantidade || "");
  const [val, setVal] = useState(valor || "");
  const [validade, setValidade] = useState(dt_validade || "");

  useEffect(() => {
    if (!open) {
      setQtd(quantidade || "");
      setVal(valor || "");
      setValidade(dt_validade || "");
    }
  }, [open, quantidade, valor, dt_validade]);

  const valorNum = Number(valor) || 0;
  const temValores = !!quantidade || valorNum > 0 || !!dt_validade;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant={temValores ? "outline" : "ghost"} size="sm">
          {temValores ? (
            <div className="flex flex-wrap gap-1">
              {quantidade && (
                <Badge variant="secondary" className="font-normal">
                  Qtd: {quantidade}
                </Badge>
              )}
              {valorNum > 0 && (
                <Badge variant="secondary" className="font-normal">
                  R$ {valorNum.toFixed(2)}
                </Badge>
              )}
              {dt_validade && (
                <Badge variant="secondary" className="font-normal">
                  Val: {dt_validade}
                </Badge>
              )}
            </div>
          ) : (
            <span className="text-muted-foreground">+ Atribuir</span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-72">
        <div className="grid gap-3">
          <div className="grid gap-1.5">
            <Label className="text-xs">Quantidade</Label>
            <Input
              type="number"
              value={qtd}
              onChange={(e) => setQtd(e.target.value)}
              min={0}
            />
          </div>
          <div className="grid gap-1.5">
            <Label className="text-xs">Valor (R$)</Label>
            <Input
              type="number"
              step="0.01"
              value={val}
              onChange={(e) => setVal(e.target.value)}
              min={0}
            />
          </div>
          <div className="grid gap-1.5">
            <Label className="text-xs">Validade</Label>
            <Input
              type="date"
              value={dataParaInput(validade)}
              onChange={(e) => setValidade(e.target.value)}
            />
          </div>
          <Button
            size="sm"
            onClick={() => {
              setXmlItens((prev) =>
                prev.map((item, idx) =>
                  idx === row.index
                    ? {
                        ...item,
                        quantidade: qtd,
                        valor: val,
                        dt_validade: validade,
                      }
                    : item,
                ),
              );
              setOpen(false);
            }}
          >
            Salvar
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

function ImportarXML() {
  const { t } = useTranslation();
  const { setIsLoading } = useGlobalLoader();
  const { numeroDeImpressoes } = useContaConfig();
  const { isLoading } = useGlobalLoader();
  const { contaConfig } = useContaConfig();
  const { impressaoConfig, coletarBlocosEPC } = useImpressaoConfig();
  const { coletaCategorias, criarCategorias, coletarCategoriasEspecificas } =
    useCategorias();
  const { coletaSeriais, seriais, adicionarMaisUmNosSeriais } = useSeriais();
  const { coletaLocalizacoes, localizacoes, criarLocalizacoesNV1 } =
    useLocalizacoes();
  const { gerarEpcs } = useEpc();
  const { conferirTagsCadastradas } = useItem();
  const { adicionarItensNaFila } = useImpressao();
  const { requireAdmin } = useContext(AdminContext)!;

  const [xmlItens, setXmlItens] = useState<XmlItem[]>([]);
  const [permitirItensRepetidos, setPermitirItensRepetidos] = useState(false);
  const [epcRepetidosDialog, setEpcRepetidosDialog] = useState(false);
  const [epcRepetidosEncontrados, setEpcRepetidosEncontrados] = useState<
    Array<{ epc: string; codigo: string }>
  >([]);
  const [codigosConvertidos, setCodigosConvertidos] = useState<
    Array<{ original: string; convertido: string }>
  >([]);
  const [fileName, setFileName] = useState<string>("");
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [localizacaoSelecionada, setLocalizacaoSelecionada] = useState("");
  const [localizacaoSelecionada2, setLocalizacaoSelecionada2] = useState("");
  const [localizacaoSelecionada3, setLocalizacaoSelecionada3] = useState("");
  const [localizacaoSelecionada4, setLocalizacaoSelecionada4] = useState("");
  // Unidade de medida digitada (única para toda a importação) — vai para a categoria.
  const [unidadeMedida, setUnidadeMedida] = useState("");

  // ---- Preferência persistida (chave consistente) ----
  useEffect(() => {
    const checked = localStorage.getItem("permitirItensRepetidos");
    setPermitirItensRepetidos(checked === "true");
  }, []);

  // ---- Localização padrão ----
  useEffect(() => {
    if (impressaoConfig) {
      setLocalizacaoSelecionada(impressaoConfig.localizacao_padrao || "");
      setLocalizacaoSelecionada2(impressaoConfig.localizacao_padrao_2 || "");
      setLocalizacaoSelecionada3(impressaoConfig.localizacao_padrao_3 || "");
      setLocalizacaoSelecionada4(impressaoConfig.localizacao_padrao_4 || "");
    }
  }, [impressaoConfig]);

  // ---- Colunas ----
  const buildColumns = (): ColumnDef<XmlItem>[] => {
    if (!impressaoConfig) return [];

    const cols: ColumnDef<XmlItem>[] = [
      {
        accessorKey: "codigo",
        header: impressaoConfig.campo_codigo_xml
          ? t("xml.codeField", { campo: impressaoConfig.campo_codigo_xml })
          : t("xml.code"),
        cell: ({ row }) => {
          const original = row.getValue("codigo") as string;
          const hash = sanitizarCodigoHex(original);
          const convertido = hash !== original.toUpperCase();
          return (
            <div className="flex items-center gap-1.5">
              <span className="text-xs">{hash}</span>
              {convertido && (
                <Badge
                  variant="outline"
                  className="text-[10px] text-amber-700 dark:text-amber-400 border-amber-500/30 bg-amber-500/10 gap-1"
                >
                  <AlertTriangle className="h-3 w-3" />
                  {original}
                </Badge>
              )}
            </div>
          );
        },
      },
      {
        accessorKey: "descricao",
        header: impressaoConfig.campo_descricao_xml
          ? t("xml.descField", { campo: impressaoConfig.campo_descricao_xml })
          : impressaoConfig.campo_codigo_xml
            ? t("xml.descBase")
            : t("xml.description"),
      },
    ];

    const infoCols: Array<{
      key: keyof XmlItem;
      campo: string;
      label: string;
    }> = [
      { key: "inf_compl_1", campo: impressaoConfig.campo_info1_xml, label: t("xml.info", { n: 1 }) },
      { key: "inf_compl_2", campo: impressaoConfig.campo_info2_xml, label: t("xml.info", { n: 2 }) },
      { key: "inf_compl_3", campo: impressaoConfig.campo_info3_xml, label: t("xml.info", { n: 3 }) },
      { key: "inf_compl_4", campo: impressaoConfig.campo_info4_xml, label: t("xml.info", { n: 4 }) },
      { key: "inf_compl_5", campo: impressaoConfig.campo_info5_xml, label: t("xml.info", { n: 5 }) },
      { key: "inf_compl_6", campo: impressaoConfig.campo_info6_xml, label: t("xml.info", { n: 6 }) },
      { key: "inf_compl_7", campo: impressaoConfig.campo_info7_xml, label: t("xml.info", { n: 7 }) },
      { key: "inf_compl_8", campo: impressaoConfig.campo_info8_xml, label: t("xml.info", { n: 8 }) },
      { key: "inf_compl_9", campo: impressaoConfig.campo_info9_xml, label: t("xml.info", { n: 9 }) },
      { key: "inf_compl_10", campo: impressaoConfig.campo_info10_xml, label: t("xml.info", { n: 10 }) },
      { key: "inf_compl_11", campo: impressaoConfig.campo_info11_xml, label: t("xml.info", { n: 11 }) },
      { key: "inf_compl_12", campo: impressaoConfig.campo_info12_xml, label: t("xml.info", { n: 12 }) },
    ];

    for (const { key, campo, label } of infoCols) {
      if (xmlItens.some((item) => item[key] !== "")) {
        const header = campo
          ? t("xml.withSource", { label, campo })
          : t("xml.withBase", { label });
        cols.push({ accessorKey: key, header });
      }
    }

    // Qtd/valor/validade por item, editáveis — substitui as antigas colunas
    // somente-leitura de Valor e Validade.
    cols.push({
      id: "info_individual",
      header: "Info individual",
      cell: ({ row }) => (
        <XmlInfoIndividualCell row={row} setXmlItens={setXmlItens} />
      ),
    });

    return cols;
  };

  // ---- XML parsing ----
  async function parseXmlFile(xmlContent: string): Promise<XmlItem[]> {
    if (!impressaoConfig) {
      alert.error(t("xml.configNotFound"));
      return [];
    }

    const campoCodigo = impressaoConfig.campo_codigo_xml?.trim();
    const campoDescricao = impressaoConfig.campo_descricao_xml?.trim();

    if (!campoCodigo) {
      alert.error(t("xml.codeFieldNotConfigured"));
      return [];
    }

    let doc: Document;
    try {
      const parser = new DOMParser();
      doc = parser.parseFromString(xmlContent, "application/xml");
      const parseError = doc.querySelector("parsererror");
      if (parseError) {
        alert.error(t("xml.invalidXml", { erro: parseError.textContent }));
        return [];
      }
    } catch {
      alert.error(t("xml.processErrorGeneric"));
      return [];
    }

    const allElements = Array.from(doc.querySelectorAll("*"));
    const withField = allElements.filter(
      (el) => el.querySelector(campoCodigo) !== null,
    );
    const itemElements = withField.filter(
      (el) => !withField.some((other) => other !== el && el.contains(other)),
    );

    if (itemElements.length === 0) {
      alert.error(t("xml.noElementFound", { campo: campoCodigo }));
      return [];
    }

    // Os campos configurados podem apontar pra tags do cabeçalho da nota
    // (nNF, serie, emitente...), que ficam fora do elemento do item.
    const buscarTexto = (el: Element, tagName?: string | null) =>
      getElementTextComEscopo(el, tagName?.trim() ?? "", itemElements);

    const campoQtdeTags = impressaoConfig.campo_qtde_tags_xml?.trim();

    // ---- Macros (operações) nos campos numéricos ----
    const macroQtde = analisarMacroXml(impressaoConfig.campo_qtde_xml);
    const macroValor = analisarMacroXml(impressaoConfig.campo_valor_xml);
    const macroValorUnidade = analisarMacroXml(
      impressaoConfig.campo_valor_unidade_xml,
    );
    const macroQtdeTags = analisarMacroXml(campoQtdeTags);

    // Só busca categorias na base quando alguma macro usa info.compl.N.
    let categoriasPorEan = new Map<string, CategoriaItemResponse>();
    if (
      [macroQtde, macroValor, macroValorUnidade, macroQtdeTags].some(
        macroUsaInfoCategoria,
      )
    ) {
      const codigosUnicos = Array.from(
        new Set(
          itemElements
            .map((el) => sanitizarCodigoHex(getElementText(el, campoCodigo)))
            .filter(Boolean),
        ),
      );
      const cats = await coletarCategoriasEspecificas(codigosUnicos);
      categoriasPorEan = new Map(cats.map((c) => [c.ean, c]));
    }

    const criarResolverMacro = (el: Element) => {
      const categoria = categoriasPorEan.get(
        sanitizarCodigoHex(getElementText(el, campoCodigo)),
      );
      return (op: OperandoMacro): number | null => {
        if (op.tipo === "numero") return op.valor;
        if (op.tipo === "tag") return numeroDeTexto(buscarTexto(el, op.nome));
        const valor = (categoria as Record<string, unknown> | undefined)?.[
          `inf_compl_${op.indice}`
        ];
        return numeroDeTexto(valor == null ? "" : String(valor));
      };
    };

    // Resolve um campo numérico: macro (quando configurada) > tag configurada
    // > tags padrão > valor padrão.
    const resolverCampoNumerico = (
      el: Element,
      macro: MacroXml | null,
      campoConfig: string | undefined,
      tagsPadrao: string[],
      padrao: string,
    ): string => {
      if (macro) {
        const v = avaliarMacroComFallback(macro, criarResolverMacro(el));
        return v !== null ? formatarResultadoMacro(v) : padrao;
      }
      if (campoConfig?.trim()) {
        return buscarTexto(el, campoConfig) || padrao;
      }
      for (const tag of tagsPadrao) {
        const v = buscarTexto(el, tag);
        if (v) return v;
      }
      return padrao;
    };

    // Info. complementar sem valor no XML cai na pré-definição do campo quando
    // ela tem uma única opção cadastrada (não há o que escolher).
    const pairs: { item: XmlItem; el: Element }[] = itemElements.map((el) => ({
      el,
      item: aplicarPreDefinicoesUnicas(impressaoConfig, {
        codigo: getElementText(el, campoCodigo),
        descricao: campoDescricao ? buscarTexto(el, campoDescricao) : "",
        inf_compl_1: buscarTexto(el, impressaoConfig.campo_info1_xml),
        inf_compl_2: buscarTexto(el, impressaoConfig.campo_info2_xml),
        inf_compl_3: buscarTexto(el, impressaoConfig.campo_info3_xml),
        inf_compl_4: buscarTexto(el, impressaoConfig.campo_info4_xml),
        inf_compl_5: buscarTexto(el, impressaoConfig.campo_info5_xml),
        inf_compl_6: buscarTexto(el, impressaoConfig.campo_info6_xml),
        inf_compl_7: buscarTexto(el, impressaoConfig.campo_info7_xml),
        inf_compl_8: buscarTexto(el, impressaoConfig.campo_info8_xml),
        inf_compl_9: buscarTexto(el, impressaoConfig.campo_info9_xml),
        inf_compl_10: buscarTexto(el, impressaoConfig.campo_info10_xml),
        inf_compl_11: buscarTexto(el, impressaoConfig.campo_info11_xml),
        inf_compl_12: buscarTexto(el, impressaoConfig.campo_info12_xml),
        quantidade: resolverCampoNumerico(
          el,
          macroQtde,
          impressaoConfig.campo_qtde_xml,
          ["QUANTIDADE", "quantidade"],
          "1",
        ),
        valor: resolverCampoNumerico(
          el,
          macroValor,
          impressaoConfig.campo_valor_xml,
          ["VALOR", "valor"],
          "0",
        ),
        valor_unidade: resolverCampoNumerico(
          el,
          macroValorUnidade,
          impressaoConfig.campo_valor_unidade_xml,
          [],
          "0",
        ),
        dt_validade: impressaoConfig.campo_validade_xml
          ? buscarTexto(el, impressaoConfig.campo_validade_xml) || ""
          : buscarTexto(el, "DT_VALIDADE") ||
            buscarTexto(el, "dt_validade") ||
            "",
      }),
    }));

    const validPairs = pairs.filter(({ item }) => item.codigo !== "");
    if (validPairs.length === 0) {
      alert.error(t("xml.noValidItem"));
      return [];
    }

    if (campoDescricao) {
      const semDescricao = validPairs.filter(({ item }) => !item.descricao);
      if (semDescricao.length > 0) {
        alert.warning(
          t("xml.someWithoutDesc", { count: semDescricao.length }),
        );
      }
    }

    const expandedItems: XmlItem[] = campoQtdeTags
      ? validPairs.flatMap(({ item, el }) => {
          let repeat = 1;
          if (macroQtdeTags) {
            // Arredonda pra cima (com tolerância a ruído de float): uma
            // sobra parcial ainda precisa de etiqueta.
            const v = avaliarMacroComFallback(
              macroQtdeTags,
              criarResolverMacro(el),
            );
            repeat = v !== null && v > 0 ? Math.ceil(v - 1e-9) : 1;
          } else {
            const rawQtd = buscarTexto(el, campoQtdeTags);
            const qtd = parseInt(rawQtd, 10);
            repeat = Number.isFinite(qtd) && qtd > 0 ? qtd : 1;
          }
          return Array.from({ length: repeat }, () => ({ ...item }));
        })
      : validPairs.map(({ item }) => item);

    const convertidos = expandedItems
      .filter((item) => !/^[0-9A-Fa-f]+$/.test(item.codigo))
      .map((item) => ({
        original: item.codigo,
        convertido: sanitizarCodigoHex(item.codigo),
      }))
      .filter(
        (v, i, arr) => arr.findIndex((x) => x.original === v.original) === i,
      );
    setCodigosConvertidos(convertidos);

    return expandedItems;
  }

  // ---- Arquivo ----
  const processarArquivo = (file: File) => {
    if (!file.name.toLowerCase().endsWith(".xml")) {
      alert.error(t("xml.onlyXml"));
      return;
    }
    const reader = new FileReader();
    reader.onload = async (event) => {
      const xmlData = event.target?.result as string;
      const parsed = await parseXmlFile(xmlData);
      if (parsed.length === 0) return;

      const campoCodigo = impressaoConfig?.campo_codigo_xml?.trim();
      const campoDescricao = impressaoConfig?.campo_descricao_xml?.trim();

      // Quando descrição não vem do XML (apenas código), busca na base e
      // preenche descrição + infos complementares dos campos não configurados.
      if (campoCodigo && !campoDescricao && impressaoConfig) {
        setIsLoading(true);
        try {
          const codigosUnicos = Array.from(
            new Set(parsed.map((item) => sanitizarCodigoHex(item.codigo))),
          );
          const categoriasBase =
            await coletarCategoriasEspecificas(codigosUnicos);

          const naoEncontrados = codigosUnicos.filter(
            (cod) => !categoriasBase.some((c) => c.ean === cod),
          );
          if (naoEncontrados.length > 0) {
            alert.error(
              t("xml.categoriesNotFound", {
                codigos: naoEncontrados.join(", "),
              }),
            );
            return;
          }

          const camposInfoXml = [
            impressaoConfig.campo_info1_xml?.trim(),
            impressaoConfig.campo_info2_xml?.trim(),
            impressaoConfig.campo_info3_xml?.trim(),
            impressaoConfig.campo_info4_xml?.trim(),
            impressaoConfig.campo_info5_xml?.trim(),
            impressaoConfig.campo_info6_xml?.trim(),
            impressaoConfig.campo_info7_xml?.trim(),
            impressaoConfig.campo_info8_xml?.trim(),
            impressaoConfig.campo_info9_xml?.trim(),
            impressaoConfig.campo_info10_xml?.trim(),
            impressaoConfig.campo_info11_xml?.trim(),
            impressaoConfig.campo_info12_xml?.trim(),
          ];

          // A categoria da base tem prioridade sobre a pré-definição, então o
          // preenchimento das opções únicas é refeito depois do enriquecimento
          // (o campo pode ter vindo vazio da categoria).
          const enriquecidos: XmlItem[] = parsed.map((item) => {
            const codSan = sanitizarCodigoHex(item.codigo);
            const cat = categoriasBase.find((c) => c.ean === codSan);
            if (!cat) return item;
            // Informação individual padrão da categoria preenche o que o XML
            // não trouxe (o valor do XML tem prioridade).
            const [comPadraoCategoria] = aplicarInfoIndividual(
              [item],
              valoresDaCategoria(cat),
              CAMPOS_XML,
              true,
            );
            return aplicarPreDefinicoesUnicas(impressaoConfig, {
              ...comPadraoCategoria,
              descricao: cat.descricao,
              inf_compl_1: camposInfoXml[0] ? item.inf_compl_1 : cat.inf_compl_1,
              inf_compl_2: camposInfoXml[1] ? item.inf_compl_2 : cat.inf_compl_2,
              inf_compl_3: camposInfoXml[2] ? item.inf_compl_3 : cat.inf_compl_3,
              inf_compl_4: camposInfoXml[3] ? item.inf_compl_4 : cat.inf_compl_4,
              inf_compl_5: camposInfoXml[4] ? item.inf_compl_5 : cat.inf_compl_5,
              inf_compl_6: camposInfoXml[5] ? item.inf_compl_6 : cat.inf_compl_6,
              inf_compl_7: camposInfoXml[6] ? item.inf_compl_7 : cat.inf_compl_7,
              inf_compl_8: camposInfoXml[7] ? item.inf_compl_8 : cat.inf_compl_8,
              inf_compl_9: camposInfoXml[8] ? item.inf_compl_9 : cat.inf_compl_9,
              inf_compl_10: camposInfoXml[9]
                ? item.inf_compl_10
                : cat.inf_compl_10,
              inf_compl_11: camposInfoXml[10]
                ? item.inf_compl_11
                : cat.inf_compl_11,
              inf_compl_12: camposInfoXml[11]
                ? item.inf_compl_12
                : cat.inf_compl_12,
            });
          });

          setXmlItens(enriquecidos);
          setFileName(file.name);
          alert.success(
            t("xml.importSuccessN", { count: enriquecidos.length }),
          );
        } finally {
          setIsLoading(false);
        }
        return;
      }

      setXmlItens(parsed);
      setFileName(file.name);
      alert.success(t("xml.importSuccessN", { count: parsed.length }));
    };
    reader.readAsText(file);
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      processarArquivo(e.target.files[0]);
    } else {
      alert.error(t("xml.noFileSelected"));
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    if (xmlItens.length > 0 || isLoading) return;
    const file = e.dataTransfer.files?.[0];
    if (file) processarArquivo(file);
  };

  const limparXml = () => {
    setXmlItens([]);
    setCodigosConvertidos([]);
    setFileName("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  // ---- Sincronizar ----
  const sincronizar = async () => {
    await coletaCategorias();
    await coletaSeriais();
    await coletaLocalizacoes();
    setXmlItens([]);
    alert.success("Dados sincronizados com sucesso!");
  };

  // ---- Gerar lista pra impressão ----
  const gerarListaParaImpressao = async () => {
    if (!xmlItens.length) {
      alert.error("Nenhum item no XML para gerar impressão.");
      return;
    }
    if (!impressaoConfig) {
      alert.error("Configuração de impressão não encontrada.");
      return;
    }
    if (!impressaoConfig?.app_id) {
      alert.error(
        "Informe o Hardware ID nas configurações para realizar a impressão.",
      );
      return;
    }
    if (!localizacaoSelecionada) {
      alert.error("Selecione ao menos a Localização Nv.1.");
      return;
    }

    setIsLoading(true);

    try {
      const categoriasParaCriar: NewCategoriaItem[] = [];
      const eanHashSeen = new Set<string>();

      for (const item of xmlItens) {
        const eanHash = sanitizarCodigoHex(item.codigo);
        if (!eanHashSeen.has(eanHash)) {
          eanHashSeen.add(eanHash);
          categoriasParaCriar.push({
            descricao: item.descricao,
            ean: eanHash,
            id_conta: impressaoConfig.id_conta,
            unidade_medida: unidadeMedida,
            inf_compl_1: item.inf_compl_1,
            inf_compl_2: item.inf_compl_2,
            inf_compl_3: item.inf_compl_3,
            inf_compl_4: item.inf_compl_4,
            inf_compl_5: item.inf_compl_5,
            inf_compl_6: item.inf_compl_6,
            inf_compl_7: item.inf_compl_7,
            inf_compl_8: item.inf_compl_8,
            inf_compl_9: item.inf_compl_9,
            inf_compl_10: item.inf_compl_10,
            inf_compl_11: item.inf_compl_11,
            inf_compl_12: item.inf_compl_12,
          });
        }
      }

      const categoriasCriadas = await criarCategorias(categoriasParaCriar);
      if (!categoriasCriadas || categoriasCriadas.length === 0) {
        alert.error("Falha ao criar/obter categorias. Tente novamente.");
        return;
      }

      let idLocalizacao1 = "";
      let idLocalizacao2 = "";
      let idLocalizacao3 = "";
      let idLocalizacao4 = "";

      const loc1Existente = localizacoes.find(
        (l) => l.descricao === localizacaoSelecionada,
      );

      if (loc1Existente) {
        idLocalizacao1 = loc1Existente._id;
      } else if (localizacaoSelecionada) {
        const novas = await criarLocalizacoesNV1([
          {
            ativo: 1,
            cor: "",
            descricao: localizacaoSelecionada,
            id_conta: impressaoConfig.id_conta,
            loc_baixados: false,
            observacao: "",
            tag: "",
          },
        ]);
        if (novas && novas.length > 0) idLocalizacao1 = novas[0]._id;
      }

      if (localizacaoSelecionada2) {
        const loc2Existente = localizacoes
          .flatMap((l) => l.children)
          .find(
            (l) =>
              l.descricao === localizacaoSelecionada2 &&
              l.id_nivel_loc1 === idLocalizacao1,
          );
        idLocalizacao2 = loc2Existente?._id || "";
      }

      if (localizacaoSelecionada3) {
        const loc3Existente = localizacoes
          .flatMap((l) => l.children)
          .flatMap((l) => l.children)
          .find(
            (l) =>
              l.descricao === localizacaoSelecionada3 &&
              l.id_nivel_loc2 === idLocalizacao2,
          );
        idLocalizacao3 = loc3Existente?._id || "";
      }

      if (localizacaoSelecionada4) {
        const loc4Existente = localizacoes
          .flatMap((l) => l.children)
          .flatMap((l) => l.children)
          .flatMap((l) => l.children)
          .find(
            (l) =>
              l.descricao === localizacaoSelecionada4 &&
              l.id_nivel_loc3 === idLocalizacao3,
          );
        idLocalizacao4 = loc4Existente?._id || "";
      }

      const listaParaImpressao: ImpressaoItem[] = [];
      const serialPorEan: Record<string, number> = {};
      let numero_de_impressoes = numeroDeImpressoes || 0;

      for (const item of xmlItens) {
        const eanHash = sanitizarCodigoHex(item.codigo);

        const categoria = categoriasCriadas.find((c) => c.ean === eanHash);
        if (!categoria) {
          alert.error(
            `Categoria com código ${item.codigo} não encontrada após criação em batch.`,
          );
          continue;
        }
        let serialAtual = serialPorEan[eanHash];
        if (serialAtual === undefined) {
          serialAtual =
            (seriais.find((s) => s.gtin === eanHash)?.serial ?? -1) + 1;
        }

        const localizacoesZpl: string[] = [
          localizacaoSelecionada,
          localizacaoSelecionada2,
          localizacaoSelecionada3,
          localizacaoSelecionada4,
        ];

        const epcGerado = gerarEpcs(
          {
            _id: categoria._id,
            descricao: item.descricao,
            ean: categoria.ean,
            id_conta: categoria.id_conta,
            valor_unidade: Number(item.valor_unidade) || 0,
            unidade_medida: unidadeMedida,
            inf_compl_1: item.inf_compl_1,
            inf_compl_2: item.inf_compl_2,
            inf_compl_3: item.inf_compl_3,
            inf_compl_4: item.inf_compl_4,
            inf_compl_5: item.inf_compl_5,
            inf_compl_6: item.inf_compl_6,
            inf_compl_7: item.inf_compl_7,
            inf_compl_8: item.inf_compl_8,
            inf_compl_9: item.inf_compl_9,
            inf_compl_10: item.inf_compl_10,
            inf_compl_11: item.inf_compl_11,
            inf_compl_12: item.inf_compl_12,
          },
          1,
          localizacoesZpl,
          coletarBlocosEPC(),
          serialAtual,
        )[0];

        if (!epcGerado) {
          alert.error(
            `Não foi possível gerar o EPC do produto ${item.codigo}${
              item.descricao ? ` - ${item.descricao}` : ""
            }. Ajuste os dados desse item no XML e tente novamente.`,
          );
          return;
        }

        serialPorEan[eanHash] = serialAtual + 1;

        listaParaImpressao.push({
          _id: epcGerado._id,
          inf_compl_1: verificarVariaveisDeSubstituicaoEmInfosComplementares(
            epcGerado,
            item.inf_compl_1,
          ),
          inf_compl_2: verificarVariaveisDeSubstituicaoEmInfosComplementares(
            epcGerado,
            item.inf_compl_2,
          ),
          inf_compl_3: verificarVariaveisDeSubstituicaoEmInfosComplementares(
            epcGerado,
            item.inf_compl_3,
          ),
          inf_compl_4: verificarVariaveisDeSubstituicaoEmInfosComplementares(
            epcGerado,
            item.inf_compl_4,
          ),
          inf_compl_5: verificarVariaveisDeSubstituicaoEmInfosComplementares(
            epcGerado,
            item.inf_compl_5,
          ),
          inf_compl_6: verificarVariaveisDeSubstituicaoEmInfosComplementares(
            epcGerado,
            item.inf_compl_6,
          ),
          inf_compl_7: verificarVariaveisDeSubstituicaoEmInfosComplementares(
            epcGerado,
            item.inf_compl_7,
          ),
          inf_compl_8: verificarVariaveisDeSubstituicaoEmInfosComplementares(
            epcGerado,
            item.inf_compl_8,
          ),
          inf_compl_9: verificarVariaveisDeSubstituicaoEmInfosComplementares(
            epcGerado,
            item.inf_compl_9,
          ),
          inf_compl_10: verificarVariaveisDeSubstituicaoEmInfosComplementares(
            epcGerado,
            item.inf_compl_10,
          ),
          inf_compl_11: item.inf_compl_11,
          inf_compl_12: item.inf_compl_12,
          descricao: item.descricao,
          codigo: eanHash,
          tag: epcGerado.epc,
          zpl_impressao: epcGerado.zpl_impressao_bo,
          id_categoria: categoria._id,
          id_conta: impressaoConfig.id_conta,
          id_usuario: impressaoConfig.id_usuario,
          id_localizacao1: idLocalizacao1,
          id_localizacao2: idLocalizacao2,
          id_localizacao3: idLocalizacao3,
          id_localizacao4: idLocalizacao4,
          valor: Number(item.valor) || 0,
          valor_unidade: Number(item.valor_unidade) || 0,
          quantidade: item.quantidade || "1",
          dt_validade: brDateToISO(item.dt_validade) || "",
          n_impressao: numero_de_impressoes.toString(),
          id_externo: item.codigo,
          ultima_localizacao: {
            primeira_vez: true,
            verificar: contaConfig?.transferencia_loc_origem || false,
          },
        });

        numero_de_impressoes++;
      }

      if (listaParaImpressao.length === 0) {
        alert.error(t("xml.noEpcGenerated"));
        return;
      }

      if (!permitirItensRepetidos) {
        const itensNaBase = await conferirTagsCadastradas(
          listaParaImpressao.map((item) => item.tag),
        );
        if (itensNaBase.length > 0) {
          const repetidosFormatados = listaParaImpressao
            .filter((item) => itensNaBase.some((i) => i.tag === item.tag))
            .map((item) => ({ epc: item.tag, codigo: item.codigo }));
          setEpcRepetidosEncontrados(repetidosFormatados);
          setEpcRepetidosDialog(true);
          adicionarMaisUmNosSeriais(
            repetidosFormatados.map((item) => sanitizarCodigoHex(item.codigo)),
          );
          return;
        }
      }

      if (verificarEpcsInvalidos(listaParaImpressao.map((item) => item.tag))) {
        alert.error(
          "EPCs inválidos encontrados. Verifique os dados e tente novamente.",
        );
        return;
      }

      adicionarItensNaFila(
        listaParaImpressao,
        undefined,
        undefined,
        MODOS_IMPRESSAO.XML,
      );
      setXmlItens([]);
      setFileName("");
    } catch (error) {
      console.error("Erro ao gerar lista para impressão:", error);
      alert.error(
        `Erro ao gerar impressão: ${error instanceof Error ? error.message : String(error)}`,
      );
    } finally {
      setIsLoading(false);
    }
  };

  const columns = buildColumns();

  return (
    <div className="flex flex-col gap-4 py-4 md:gap-6 md:py-6 px-4 lg:px-6">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <h2 className="text-xl font-semibold tracking-tight">
            Importar XML
          </h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            Envie um XML (NFe, ordens etc.) e gere etiquetas a partir dos itens
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-2 rounded-md border bg-card px-3 h-9">
            <Switch
              id="xml-repetidos"
              disabled={isLoading}
              checked={permitirItensRepetidos}
              onCheckedChange={(checked) => {
                requireAdmin(() => {
                  setPermitirItensRepetidos(checked);
                  localStorage.setItem(
                    "permitirItensRepetidos",
                    String(checked),
                  );
                });
              }}
            />
            <Label htmlFor="xml-repetidos" className="text-xs cursor-pointer">
              {t("sscc.allowRepeated")}
            </Label>
          </div>

          <Button
            variant="outline"
            size="sm"
            onClick={sincronizar}
            disabled={isLoading}
          >
            <RefreshCw
              className={`h-4 w-4 ${isLoading ? "animate-spin" : ""}`}
            />
            <span className="hidden sm:inline">Sincronizar</span>
          </Button>

          <Button
            size="sm"
            onClick={gerarListaParaImpressao}
            disabled={xmlItens.length === 0 || isLoading}
            title={
              xmlItens.length === 0
                ? "Importe um XML pra imprimir"
                : !localizacaoSelecionada
                  ? "Selecione uma localização"
                  : `Imprimir ${xmlItens.length} etiqueta${xmlItens.length === 1 ? "" : "s"}`
            }
          >
            <Printer className="h-4 w-4" />
            <span className="hidden sm:inline">Imprimir</span>
            {xmlItens.length > 0 && (
              <Badge
                variant="secondary"
                className="ml-1 h-5 px-1.5 text-[10px] bg-white/20 text-primary-foreground hover:bg-white/20"
              >
                {xmlItens.length}
              </Badge>
            )}
          </Button>
        </div>
      </div>

      {/* 1. Importar arquivo */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <div className="flex h-7 w-7 items-center justify-center rounded-full bg-primary/10 text-primary text-xs font-semibold">
              1
            </div>
            <CardTitle className="text-sm font-medium flex items-center gap-1.5">
              <FileCode2 className="h-4 w-4 text-muted-foreground" />
              {t("xml.importXmlFile")}
            </CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          {xmlItens.length === 0 ? (
            <div
              onDragOver={(e) => {
                e.preventDefault();
                if (!isLoading) setIsDragging(true);
              }}
              onDragLeave={() => setIsDragging(false)}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              className={`flex flex-col items-center justify-center gap-3 rounded-md border-2 border-dashed py-10 px-4 cursor-pointer transition-colors ${
                isDragging
                  ? "border-primary bg-primary/5"
                  : "border-border bg-muted/20 hover:bg-muted/40 hover:border-primary/40"
              } ${isLoading ? "pointer-events-none opacity-60" : ""}`}
            >
              <div
                className={`flex h-12 w-12 items-center justify-center rounded-full transition-colors ${
                  isDragging ? "bg-primary/15" : "bg-muted"
                }`}
              >
                <Upload
                  className={`h-6 w-6 ${
                    isDragging ? "text-primary" : "text-muted-foreground"
                  }`}
                />
              </div>
              <div className="text-center space-y-1">
                <p className="text-sm font-medium">
                  {isDragging
                    ? "Solte o arquivo aqui"
                    : "Arraste o XML ou clique para selecionar"}
                </p>
                <p className="text-xs text-muted-foreground">
                  Aceita apenas arquivos{" "}
                  <code className="px-1 py-0.5 rounded bg-muted text-[10px]">
                    .xml
                  </code>{" "}
                  · configure os campos em Preferências &gt; XML
                </p>
              </div>
              <input
                ref={fileInputRef}
                id="importar-xml-file"
                type="file"
                accept=".xml"
                className="sr-only"
                onChange={handleFileInputChange}
                disabled={isLoading}
              />
            </div>
          ) : (
            <div className="flex items-center justify-between gap-3 rounded-md border bg-muted/30 p-3">
              <div className="flex items-center gap-3 min-w-0">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-emerald-500/10">
                  <FileCode2 className="h-5 w-5 text-emerald-500" />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate">
                    {fileName || "Arquivo carregado"}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {xmlItens.length}{" "}
                    {xmlItens.length === 1
                      ? "item importado"
                      : "itens importados"}
                  </p>
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={limparXml}
                disabled={isLoading}
              >
                <X className="h-4 w-4" />
                {t("xml.clear")}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Aviso de conversão */}
      {codigosConvertidos.length > 0 && (
        <Card className="border-amber-500/30 bg-amber-500/5">
          <CardContent className="p-3 flex items-start gap-2 text-xs">
            <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
            <div className="flex flex-col gap-1 text-amber-700 dark:text-amber-300 min-w-0">
              <span className="font-medium">
                {codigosConvertidos.length} código(s) com caracteres fora do
                padrão hexadecimal foram convertidos automaticamente.
              </span>
              <span className="opacity-80">
                O código original é mantido para exibição, mas o EPC será gerado
                com o código convertido.
              </span>
              <ul className="mt-1 list-disc list-inside space-y-0.5 max-h-24 overflow-y-auto">
                {codigosConvertidos.map(({ original, convertido }) => (
                  <li key={original}>
                    <span>{original}</span>
                    <span className="mx-1">→</span>
                    <span className="font-medium">{convertido}</span>
                  </li>
                ))}
              </ul>
            </div>
          </CardContent>
        </Card>
      )}

      {/* 2. Localizações */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <div className="flex h-7 w-7 items-center justify-center rounded-full bg-primary/10 text-primary text-xs font-semibold">
              2
            </div>
            <CardTitle className="text-sm font-medium flex items-center gap-1.5">
              <MapPin className="h-4 w-4 text-muted-foreground" />
              Localizações
            </CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <div className="grid gap-2">
              <Label
                htmlFor="xml-loc-1"
                className="text-xs text-muted-foreground"
              >
                Nível 1 <span className="text-destructive">*</span>
              </Label>
              <LocalizacaoCombobox
                id="xml-loc-1"
                disabled={isLoading}
                opcoes={localizacoes}
                value={localizacaoSelecionada}
                onChange={(v) => {
                  setLocalizacaoSelecionada(v);
                  setLocalizacaoSelecionada2("");
                  setLocalizacaoSelecionada3("");
                  setLocalizacaoSelecionada4("");
                }}
                placeholder="Não definido"
                valorPor="descricao"
              />
            </div>

            <div className="grid gap-2">
              <Label
                htmlFor="xml-loc-2"
                className="text-xs text-muted-foreground"
              >
                Nível 2
              </Label>
              <LocalizacaoCombobox
                id="xml-loc-2"
                disabled={isLoading || !localizacaoSelecionada}
                opcoes={localizacoes
                  .find((l) => l.descricao === localizacaoSelecionada)
                  ?.children}
                value={localizacaoSelecionada2}
                onChange={(v) => {
                  setLocalizacaoSelecionada2(v);
                  setLocalizacaoSelecionada3("");
                  setLocalizacaoSelecionada4("");
                }}
                placeholder="Não definido"
                valorPor="descricao"
              />
            </div>

            <div className="grid gap-2">
              <Label
                htmlFor="xml-loc-3"
                className="text-xs text-muted-foreground"
              >
                Nível 3
              </Label>
              <LocalizacaoCombobox
                id="xml-loc-3"
                disabled={
                  isLoading ||
                  !localizacaoSelecionada ||
                  !localizacaoSelecionada2
                }
                opcoes={localizacoes
                  .find((l) => l.descricao === localizacaoSelecionada)
                  ?.children.find(
                    (l2) => l2.descricao === localizacaoSelecionada2,
                  )
                  ?.children}
                value={localizacaoSelecionada3}
                onChange={(v) => {
                  setLocalizacaoSelecionada3(v);
                  setLocalizacaoSelecionada4("");
                }}
                placeholder="Não definido"
                valorPor="descricao"
              />
            </div>

            <div className="grid gap-2">
              <Label
                htmlFor="xml-loc-4"
                className="text-xs text-muted-foreground"
              >
                Nível 4
              </Label>
              <LocalizacaoCombobox
                id="xml-loc-4"
                disabled={
                  isLoading ||
                  !localizacaoSelecionada ||
                  !localizacaoSelecionada2 ||
                  !localizacaoSelecionada3
                }
                opcoes={localizacoes
                  .find((l) => l.descricao === localizacaoSelecionada)
                  ?.children.find(
                    (l2) => l2.descricao === localizacaoSelecionada2,
                  )
                  ?.children.find(
                    (l3) => l3.descricao === localizacaoSelecionada3,
                  )
                  ?.children}
                value={localizacaoSelecionada4}
                onChange={(v) => {
                  setLocalizacaoSelecionada4(v);
                }}
                placeholder="Não definido"
                valorPor="descricao"
              />
            </div>
            <div className="grid gap-2">
              <Label
                htmlFor="xml-unidade-medida"
                className="text-xs text-muted-foreground"
              >
                Unidade de medida (categoria)
              </Label>
              <Select
                disabled={isLoading}
                value={unidadeMedida}
                onValueChange={(v) => setUnidadeMedida(v)}
              >
                <SelectTrigger id="xml-unidade-medida" className="w-full">
                  <SelectValue placeholder="Não definido" />
                </SelectTrigger>
                <SelectContent>
                  {UNIDADES_MEDIDA.map((u) => (
                    <SelectItem key={u} value={u}>
                      {u}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* 3. Itens importados */}
      {xmlItens.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <div className="flex h-7 w-7 items-center justify-center rounded-full bg-primary/10 text-primary text-xs font-semibold">
                  3
                </div>
                <CardTitle className="text-sm font-medium flex items-center gap-1.5">
                  <Layers className="h-4 w-4 text-muted-foreground" />
                  {t("xml.importedItems")}
                </CardTitle>
              </div>
              <Badge variant="secondary" className="font-medium">
                {t("xml.itemsCount", { count: xmlItens.length })}
              </Badge>
            </div>
          </CardHeader>
          <CardContent>
            <DataTable
              columns={columns}
              data={xmlItens}
              autoResetPageIndex={false}
            />
          </CardContent>
        </Card>
      )}

      {/* 4. Informação individual em massa */}
      {xmlItens.length > 0 && (
        <InfoIndividualEmMassa
          numero={4}
          totalItens={xmlItens.length}
          disabled={isLoading}
          onAplicar={(valores) =>
            setXmlItens((prev) => aplicarInfoIndividual(prev, valores, CAMPOS_XML))
          }
        />
      )}

      {/* AlertDialog: EPCs repetidos */}
      <AlertDialog
        open={epcRepetidosDialog}
        onOpenChange={setEpcRepetidosDialog}
      >
        <AlertDialogContent className="max-h-[90vh] overflow-hidden flex flex-col">
          <AlertDialogHeader>
            <div className="flex items-center gap-2">
              <div className="flex h-9 w-9 items-center justify-center rounded-full bg-amber-500/10">
                <AlertTriangle className="h-5 w-5 text-amber-500" />
              </div>
              <AlertDialogTitle>{t("xml.repeatedEpcsFound")}</AlertDialogTitle>
            </div>
            <AlertDialogDescription>
              {t("xml.repeatedEpcsDesc")}
            </AlertDialogDescription>
          </AlertDialogHeader>

          <div className="overflow-y-auto max-h-64 rounded-md border">
            <ul className="divide-y">
              {epcRepetidosEncontrados.map((item, index) => (
                <li
                  key={index}
                  className="flex items-center justify-between px-3 py-2 gap-2"
                >
                  <span className="text-xs truncate">{item.epc}</span>
                  <Badge variant="secondary">{item.codigo}</Badge>
                </li>
              ))}
            </ul>
          </div>

          <AlertDialogFooter>
            <AlertDialogCancel>{t("common2.cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={() => setEpcRepetidosDialog(false)}>
              <Eye className="h-4 w-4" />
              {t("common2.understood")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default ImportarXML;
